import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import FileSaver from "file-saver";
import { useTranslation } from "react-i18next";
import { DatePicker, Flex, Select } from "antd";
import { Dayjs } from "dayjs";
import Button from "../Button";
import Headline from "../Headline";
import { DownloadIcon } from "../../svg";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import {
  getGridDefaultProps,
  GRID_DEFAULT_COL_DEFS,
} from "constants/agGridConstants";
import { dateFormat, dateFormatForBE } from "components/Utils/dayjsUtils";
import { dateFormatter, dateTimeFormatter } from "helpers/agGrid/valueFormatters";
import {
  useGetOrdersForUi,
  useDownloadSelectedOrders,
  useReExportHermesOrders,
} from "api/reactQuery/queryComponents";
import {
  BSCoreServicesDtoHermesHermesOrdersHermesOrderUiResponse,
  BSDALBeerStreamEnumsHermesOrderStatus,
} from "api/reactQuery/querySchemas";

const statusTranslationKeys: Record<
  BSDALBeerStreamEnumsHermesOrderStatus,
  string
> = {
  [BSDALBeerStreamEnumsHermesOrderStatus.New]: "order_status_new",
  [BSDALBeerStreamEnumsHermesOrderStatus.Downloaded]: "order_status_downloaded",
  [BSDALBeerStreamEnumsHermesOrderStatus.Delivered]: "order_status_delivered",
  [BSDALBeerStreamEnumsHermesOrderStatus.PartiallyDelivered]:
    "order_status_partially_delivered",
  [BSDALBeerStreamEnumsHermesOrderStatus.NotDownloadedRejected]:
    "order_status_not_downloaded_rejected",
  [BSDALBeerStreamEnumsHermesOrderStatus.MissingMapping]:
    "order_status_missing_mapping",
};

const PocOrders: React.FC = () => {
  const { t } = useTranslation();
  const statusOptions = useMemo(
    () =>
      Object.values(BSDALBeerStreamEnumsHermesOrderStatus).map((value) => ({
        label: t(statusTranslationKeys[value]),
        value,
      })),
    [t],
  );
  const history = useHistory();
  const gridRef =
    useRef<
      AgGridReact<BSCoreServicesDtoHermesHermesOrdersHermesOrderUiResponse>
    >(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [statusFilter, setStatusFilter] = useState<
    BSDALBeerStreamEnumsHermesOrderStatus | undefined
  >(BSDALBeerStreamEnumsHermesOrderStatus.MissingMapping);
  const [dateCreatedFilter, setDateCreatedFilter] = useState<
    Dayjs | undefined
  >();

  const { mutate: fetchOrders, data: orders } = useGetOrdersForUi({});

  useEffect(() => {
    fetchOrders({
      body: {
        status: statusFilter,
        dateCreated: dateCreatedFilter
          ? dateCreatedFilter.format(dateFormatForBE)
          : null,
      },
    });
  }, [statusFilter, dateCreatedFilter]);

  const { mutate: downloadSelectedOrders, isLoading: isDownloading } =
    useDownloadSelectedOrders({
      onSuccess: (data: any) => {
        const { blob, headers } = data;
        let fileName = `Orders.xml`;

        const disposition = headers?.get("content-disposition");
        if (disposition) {
          fileName = disposition
            .split(";")[1]
            .split("=")[1]
            .replace(/"/g, "")
            .trim();
        }

        FileSaver.saveAs(blob, fileName);
        fetchOrders({
          body: {
            status: statusFilter,
            dateCreated: dateCreatedFilter
              ? dateCreatedFilter.format(dateFormatForBE)
              : null,
          },
        });
      },
      onError: () => {
        toast.error(t("something_went_wrong"));
      },
    });

  const { mutate: reExportOrders, isLoading: isReExporting } =
    useReExportHermesOrders({
      onSuccess: () => {
        toast.success(t("re_export_success"));
        fetchOrders({
          body: {
            status: statusFilter,
            dateCreated: dateCreatedFilter
              ? dateCreatedFilter.format(dateFormatForBE)
              : null,
          },
        });
      },
      onError: () => {
        toast.error(t("something_went_wrong"));
      },
    });

  const handleReExportSelected = () => {
    if (selectedIds.length === 0) return;
    reExportOrders({ body: selectedIds });
  };

  const columnDefs = useMemo<
    ColDef<BSCoreServicesDtoHermesHermesOrdersHermesOrderUiResponse>[]
  >(
    () => [
      {
        field: "status",
        headerName: t("status"),
        checkboxSelection: true,
        headerCheckboxSelection: true,
        valueFormatter: (params) => {
          const key =
            statusTranslationKeys[
              params.value as BSDALBeerStreamEnumsHermesOrderStatus
            ];
          return key ? t(key) : params.value;
        },
      },
      { field: "hermesOrderId", headerName: t("number") },
      {
        field: "createdDate",
        headerName: t("date_created"),
        valueFormatter: dateTimeFormatter,
      },
      { field: "depotName", headerName: t("depot") },
      { field: "customerCode", headerName: t("poc_code_short") },
      { field: "customerName", headerName: t("poc_name_short") },
      {
        field: "deliveryDate",
        headerName: t("delivery_date"),
        valueFormatter: dateFormatter,
      },
      {
        headerName: t("details"),
        maxWidth: 120,
        sortable: false,
        filter: false,

        cellStyle: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        cellRenderer: (params: ICellRendererParams) => (
          <Button
            size="small"
            style={{ padding: "4px 8px" }}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              history.push(`/poc-orders/${params.data.id}`);
            }}
          >
            {t("details")}
          </Button>
        ),
      },
    ],
    [t, history],
  );

  const handleSelectionChanged = () => {
    const selectedRows = gridRef.current?.api.getSelectedRows() || [];
    setSelectedIds(selectedRows.map((row) => row.id!));
  };

  const handleDownloadSelected = () => {
    if (selectedIds.length === 0) return;
    downloadSelectedOrders({ body: selectedIds });
  };

  return (
    <>
      <Flex justify="space-between" align="center">
        <Headline disableMargin={false}>{t("poc_orders")}</Headline>
        <Flex gap={8}>
          <Button
            size="large"
            disabled={selectedIds.length === 0 || isReExporting}
            onClick={handleReExportSelected}
          >
            {isReExporting ? "..." : t("re_export_selected_orders")}
          </Button>
          <Button
            icon={DownloadIcon}
            size="large"
            disabled={
              selectedIds.length === 0 ||
              isDownloading ||
              statusFilter ===
                BSDALBeerStreamEnumsHermesOrderStatus.MissingMapping ||
              statusFilter === BSDALBeerStreamEnumsHermesOrderStatus.New
            }
            onClick={handleDownloadSelected}
          >
            {isDownloading ? "..." : t("download_selected_orders")}
          </Button>
        </Flex>
      </Flex>

      <Flex gap={16} style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder={t("status")}
          style={{ minWidth: 200 }}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value)}
          options={statusOptions}
        />
        <DatePicker
          allowClear
          placeholder={t("date_created")}
          style={{ minWidth: 200 }}
          format={dateFormat}
          value={dateCreatedFilter}
          onChange={(date) => setDateCreatedFilter(date)}
        />
      </Flex>

      <div style={{ height: 400 }}>
        <AgGridReact
          ref={gridRef}
          getRowId={(params) => String(params.data.id)}
          rowData={orders}
          columnDefs={columnDefs}
          rowSelection="multiple"
          onSelectionChanged={handleSelectionChanged}
          defaultColDef={{
            ...GRID_DEFAULT_COL_DEFS.defaultColDef,
          }}
          {...getGridDefaultProps({ includeDefaultColDefs: false })}
        />
      </div>
    </>
  );
};

export default PocOrders;
